// The two bulk-write confirmation gates, minus the rendering.
//
// A gate is not a failure: the request was understood, nothing was written, and
// the merchant owes an answer before it will be. Both gates ask them to approve
// something destructive, and in both the PAYLOAD IS THE PROMPT — so the rule for
// "is this payload something we can honestly ask about" lives here, once. A
// dropped field is a defect to report, never a default to substitute:
// `pending_po_count || 0` or `affected_items || []` turns it into a
// complete-looking question the merchant can answer.
//
// ── The PO-impact gate ──
// HTTP 409 `{confirmation_required, pending_po_count}`: the edit would
// invalidate pending purchase order recommendations. One yes/no, answered by
// the caller re-issuing the same chunk with `confirm_po_deletion`.
//
// ── The cost-of-record gate ──
// The backend answers a save that would move an item's cost of record with
// HTTP 200 `{requires_confirmation, change_context, affected_items}` and writes
// NOTHING (backend/api/services/cost_currency_change.py).
//
// `affected_items` may be a SAMPLE: one supplier's currency edit reinterprets
// every cost it denominates, so the backend caps the rows and states the real
// scope as `change_context.affected_item_count`. A count above the rows sent is
// the truncation — there is no separate flag, and `costGateScope` is where the
// two numbers are read. The backend still applies the merchant's answer to
// every affected item, including the ones no row was sent for, which is why a
// sample may not be offered as a per-item review.
//
// Two distinct questions arrive through that one shape, and the answer token
// differs per question — see `CostChangeConfirmation.is_satisfied_by`:
//
//   adopt        — the item is getting a new default supplier, and that
//                  supplier's price becomes the item's cost of record. Every
//                  `default_supplier_change` trigger is this question. Answered
//                  with a bare `confirm_cost_change` flag. Electing an unpriced
//                  supplier moves nothing and is never gated, so an adopt row
//                  always carries a `to_cost`.
//   reinterpret  — a supplier's DefaultCurrency changed, re-denominating every
//                  cost stored against it. The merchant chooses per item how the
//                  number carries across, as a `currency_change_strategy`.
//
// <CurrencyChangeConfirmModal> renders this gate. This module is its
// non-visual half, shared with the write path (datahealth/resolveIssue.js), so
// the two cannot disagree about which question is being asked or what a given
// answer means. Nothing here renders or translates.

/**
 * Why this PO-impact payload cannot be rendered as a prompt, or `null` when it
 * can.
 *
 * The count IS the question — "N pending recommendations will be removed" — and
 * the endpoint raises this gate only when it is a positive integer
 * (`if total_po_count > 0`, backend/api/views/items.py). Anything else is a
 * dropped field, and the two ways of papering over it are both worse than
 * saying so: `|| 0` asks the merchant to approve deleting nothing, and letting
 * it through raw renders "undefined recommendations will be removed".
 *
 * @param {*} count  the body's `pending_po_count`
 * @returns {string|null}  dev-facing reason; callers translate their own copy
 */
export function poGateDefect(count) {
  if (!Number.isInteger(count) || count <= 0) {
    return `pending_po_count is not a positive integer (got ${JSON.stringify(count)})`;
  }
  return null;
}

/** The two questions `requires_confirmation` can be asking. */
export const COST_GATE = {
  ADOPT: 'adopt',
  REINTERPRET: 'reinterpret',
};

/**
 * Per-item choices in a reinterpret strategy, in the order they are offered.
 *
 * The client half of `PER_ITEM_ACTIONS` in
 * backend/api/services/cost_currency_change.py, which `_validate_strategy`
 * enforces — anything else is refused outright. Feeds the select in
 * CurrencyChangeConfirmModal, whose option labels the i18n audit enumerates
 * against that same constant.
 */
export const STRATEGY_ACTIONS = ['keep', 'convert', 'override'];

/**
 * Which question this `change_context` is asking.
 *
 * Requires a change_context — there is no mode for "no payload". Callers guard
 * with `costGatePayloadDefect` first; a gate whose payload never arrived is a
 * broken server contract, not a third mode to render.
 *
 * @param {object} changeContext  the gate body's `change_context`
 * @returns {string} one of COST_GATE
 */
export function costGateMode(changeContext) {
  if (!changeContext || typeof changeContext !== 'object') {
    throw new Error('costGateMode: change_context is required');
  }
  return changeContext.trigger === 'default_supplier_change'
    ? COST_GATE.ADOPT
    : COST_GATE.REINTERPRET;
}

/**
 * Why this gate payload cannot be rendered as a confirmation, or `null` when it
 * can.
 *
 * This is money: the merchant is being asked to approve a change to what their
 * stock is worth. A payload that arrives short — no items, no currency pair, no
 * rate, no date or source for the rate — must NOT be padded with empty strings
 * and shown anyway, because the resulting dialog reads like a complete question
 * and can be answered like one.
 * The reason string is dev-facing (log it); callers translate their own
 * copy from the fact that there IS one.
 *
 * @param {object} changeContext
 * @param {object[]} affectedItems
 * @returns {string|null}
 */
export function costGatePayloadDefect(changeContext, affectedItems) {
  if (!changeContext || typeof changeContext !== 'object' || Array.isArray(changeContext)) {
    return 'change_context is not an object';
  }
  if (!Array.isArray(affectedItems) || affectedItems.length === 0) {
    return 'affected_items is not a non-empty array';
  }
  const unidentified = affectedItems.filter((row) => !row || row.item_id == null).length;
  if (unidentified > 0) {
    // `item_id` keys the per-item choice AND is what the backend matches the
    // strategy back onto — a row without one silently drops out of the answer.
    return `${unidentified} of ${affectedItems.length} affected items carry no item_id`;
  }
  const count = changeContext.affected_item_count;
  if (!Number.isInteger(count) || count < affectedItems.length) {
    // The rows are a SAMPLE above the backend's preview cap, so their length is
    // not the scope of the change — the count is, and it is what every "N items"
    // the merchant reads comes from. Missing (or smaller than the sample it
    // arrived with) means the body cannot say what it covers, and a dialog that
    // understates its own reach is approved on the wrong facts.
    return `affected_item_count is not an integer >= the rows sent (got ${JSON.stringify(count)} for ${affectedItems.length} rows)`;
  }
  const mode = costGateMode(changeContext);
  if (mode === COST_GATE.ADOPT) {
    // Each row reads "from <cost> <currency> to <cost> <currency>": the new
    // cost and its currency always, the old currency whenever there was an old
    // cost (none at all is the item's first price).
    const short = affectedItems.filter((row) => (
      row.to_cost == null || !row.to_currency
      || (row.from_cost != null && !row.from_currency)
    )).length;
    if (short > 0) {
      return `${short} of ${affectedItems.length} adopt rows name no cost and currency to move between`;
    }
  }
  if (mode === COST_GATE.REINTERPRET) {
    // The reinterpret question IS "keep this number as it stands in X, or
    // convert it to Y at this rate". Asking it without naming X, Y or the rate
    // is asking nothing — and the rate is stated with its date.
    if (!changeContext.old_currency || !changeContext.new_currency) {
      return 'a reinterpret change_context names no currency pair';
    }
    const missing = REINTERPRET_RATE_FIELDS.filter((field) => !changeContext[field]);
    if (missing.length > 0) {
      return `a reinterpret change_context carries no ${missing.join(', ')}`;
    }
    if (changeContext.multi_currency === true) {
      // Over several pairs the header describes only the first, so each row
      // states its own pair and rate, and the dialog reads them off the row.
      const short = affectedItems.filter((row) => (
        !row.old_currency || !row.new_currency
        || REINTERPRET_RATE_FIELDS.some((field) => !row[field])
      )).length;
      if (short > 0) {
        return `${short} of ${affectedItems.length} multi-currency rows name no complete pair and rate`;
      }
    }
  }
  return null;
}

// What a reinterpret states about the rate it converts at: the rate and the day
// it applies to.
const REINTERPRET_RATE_FIELDS = ['fx_rate', 'fx_rate_date'];

/**
 * What the gate covers, versus what it sent: `{count, shown, truncated}`.
 *
 * The backend caps the rows in a confirmation body — one supplier's currency
 * edit reinterprets every cost it denominates, and on a single-supplier store
 * that is the whole catalogue. So `affected_items.length` is the size of the
 * SAMPLE and `change_context.affected_item_count` is the size of the CHANGE;
 * this is the one place that decides which one answers "how many items".
 *
 * `truncated` is what forbids offering per-item review: reviewing item by item
 * is an offer to decide every item, and a sample cannot honour it — the rows
 * nobody saw would take whatever blanket action was left selected underneath.
 *
 * Call only on a payload `costGatePayloadDefect` has cleared; it is what
 * guarantees the count is there and is at least the sample it arrived with.
 *
 * @param {object} changeContext
 * @param {object[]} affectedItems
 */
export function costGateScope(changeContext, affectedItems) {
  const shown = affectedItems.length;
  const count = changeContext.affected_item_count;
  return { count, shown, truncated: count > shown };
}

/** The answer token for an `adopt` gate. */
export function adoptFragment() {
  return { confirm_cost_change: true };
}

/**
 * The answer token for a `reinterpret` gate.
 *
 * `reviewEach` false sends the blanket choice with no per-row overrides — the
 * backend applies `default_action` to every affected item. True sends one
 * override per affected item, which is also the shape a single-item batch always
 * takes (there is no blanket choice to make over one row).
 *
 * @param {object} opts
 * @param {object[]} opts.affectedItems
 * @param {'keep'|'convert'} opts.defaultAction
 * @param {boolean} opts.reviewEach
 * @param {Object<string, {action: string, new_value?: string}>} opts.rowStrategies
 *   keyed by `item_id`; seed it with `initialRowStrategies` so every row has one.
 *   `new_value` is the text the merchant typed, in their number format.
 * @param {(raw: string) => number} opts.parseNumber  the LOCALE-AWARE parser
 *   from `useFormatters` — the server reads '1.250' as 1.25, where a Dutch
 *   merchant typed 1250.
 */
export function strategyFragment({
  affectedItems, defaultAction, reviewEach, rowStrategies, parseNumber,
}) {
  if (!reviewEach) {
    return { currency_change_strategy: { default_action: defaultAction, overrides: [] } };
  }
  const overrides = affectedItems.map((row) => {
    const strat = rowStrategies[row.item_id];
    // Unreachable via `initialRowStrategies`, and never defaulted: substituting
    // `defaultAction` here would answer a money question for a row the
    // merchant was never shown a control for.
    if (!strat) {
      throw new Error(`strategyFragment: no per-item choice for item_id=${row.item_id}`);
    }
    const entry = { item_id: row.item_id, action: strat.action };
    if (strat.action === 'override') entry.new_value = parseNumber(strat.new_value);
    return entry;
  });
  return { currency_change_strategy: { default_action: defaultAction, overrides } };
}

/** One per-item choice per affected row, all starting at `keep`. */
export function initialRowStrategies(affectedItems) {
  const initial = {};
  for (const row of affectedItems) initial[row.item_id] = { action: 'keep', new_value: '' };
  return initial;
}

/**
 * True when some row is set to `override` without a usable number — the one
 * state Apply must stay disabled in. A blank, non-numeric or negative override
 * is rejected by the backend (`_validate_strategy`), and letting it through
 * costs the merchant the whole batch. `parseNumber` is the one
 * `strategyFragment` sends with.
 */
export function hasInvalidOverride(affectedItems, rowStrategies, parseNumber) {
  return affectedItems.some((row) => {
    const strat = rowStrategies[row.item_id];
    if (!strat || strat.action !== 'override') return false;
    const parsed = parseNumber(strat.new_value);
    return !Number.isFinite(parsed) || parsed < 0;
  });
}
